'use client'

import { interpolateIfComplete, type PriceChartData } from '@/lib/c2c/transforms/price-history'

import { Chart } from './chart/chart'

interface PriceHistoryChartProps {
    chartData: PriceChartData
    cheapestTicketsTitle?: string
    cheapestTicketsDescription?: string
    priceChartSectionTitle?: string
    priceDayTitle?: string
    priceDayDescription?: string
    priceMonthTitle?: string
    priceMonthDescription?: string
    priceDateTitle?: string
    priceDateDescription?: string
}

export function PriceHistoryChart({
    chartData,
    cheapestTicketsTitle,
    cheapestTicketsDescription,
    priceChartSectionTitle,
    priceDayTitle,
    priceDayDescription,
    priceMonthTitle,
    priceMonthDescription,
    priceDateTitle,
    priceDateDescription,
}: PriceHistoryChartProps) {
    const { interpolationValues } = chartData
    // Both, not "any value resolved": these two are what the pricing copy actually
    // claims — an average fare and a saving worth acting on — and the section title
    // asserts a cheapest-tickets answer that neither exists without. ui-react gates the
    // same block on `!!cheapestPrice && !!savingsPriceUsd` (PriceChart.jsx). The
    // transform omits either key when its value is zero, so presence here means a real
    // number rather than a formatted "$0.00".
    const hasPricingInfo = Boolean(interpolationValues.price && interpolationValues.savingsPrice)

    // Resolved per string, not per section: `hasPricingInfo` only says SOME value filled,
    // so each description is dropped on its own when its own token has no value.
    const fill = (template?: string): string | null =>
        template ? interpolateIfComplete(template, interpolationValues) : null
    const cheapestTickets = fill(cheapestTicketsDescription)
    const priceDay = fill(priceDayDescription)
    const priceMonth = fill(priceMonthDescription)
    const priceDate = fill(priceDateDescription)

    const sectionTitle = hasPricingInfo ? cheapestTicketsTitle : priceChartSectionTitle

    return (
        <section
            aria-labelledby="price-chart-header"
            data-section="price-history"
            className="my-[72px] md:my-24"
        >
            {sectionTitle && (
                <h2
                    id="price-chart-header"
                    className="pb-4 text-[20px] font-bold text-[#44474a] md:text-2xl"
                >
                    {sectionTitle}
                </h2>
            )}

            {hasPricingInfo && cheapestTickets && (
                <p className="mb-6 text-[#44474a]">{cheapestTickets}</p>
            )}

            <div className="flex flex-col">
                {chartData.daily && (
                    <>
                        <Chart
                            data={chartData.daily}
                            title={priceDayTitle}
                            numVerticalTicks={3}
                            xTickValues={chartData.xTickValuesDaily}
                        />
                        {hasPricingInfo && priceDay && (
                            <p className="mb-6 text-[#44474a]">{priceDay}</p>
                        )}
                    </>
                )}

                <div className="flex flex-col md:flex-row">
                    {chartData.monthly && (
                        <div className="flex flex-col md:basis-1/2">
                            <Chart
                                data={chartData.monthly}
                                title={priceMonthTitle}
                                xTickValues={chartData.xTickValuesMonthly}
                            />
                            {hasPricingInfo && priceMonth && (
                                <p className="mb-6 text-[#44474a]">{priceMonth}</p>
                            )}
                        </div>
                    )}

                    {chartData.daysUntilDepart && (
                        <div className="flex flex-col md:ml-6 md:basis-1/2 md:first:ml-0">
                            <Chart
                                chartType="curve"
                                data={chartData.daysUntilDepart}
                                labelType="number"
                                title={priceDateTitle}
                                xTickValues={chartData.xTickValuesDepartDays}
                            />
                            {hasPricingInfo && priceDate && (
                                <p className="mb-6 text-[#44474a]">{priceDate}</p>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </section>
    )
}
